import { defineStore } from 'pinia';
import type { Ref } from 'vue';

import type { IWarehouse } from '@nova/core';
import { useLocalStorageRef } from '@satellite/composables/useLocalStorage';

const ID_STORAGE_KEY = 'warehouse.selectedWarehouseId';
const NAME_STORAGE_KEY = 'warehouse.selectedWarehouseName';
const TIMEZONE_STORAGE_KEY = 'warehouse.selectedWarehouseTimezone';

export const useWarehouseStore = defineStore('warehouse', () => {
  const { ref: selectedWarehouseId, dispose: disposeId } = useLocalStorageRef<string | null>(
    ID_STORAGE_KEY,
    null
  );
  const { ref: selectedWarehouseName, dispose: disposeName } = useLocalStorageRef<string | null>(
    NAME_STORAGE_KEY,
    null
  );
  const { ref: selectedWarehouseTimezone, dispose: disposeTimezone } = useLocalStorageRef<
    string | null
  >(TIMEZONE_STORAGE_KEY, null);

  const setSelectedWarehouse = (warehouse: Partial<IWarehouse> | null) => {
    if (warehouse) {
      selectedWarehouseId.value = warehouse.id || null;
      selectedWarehouseName.value = warehouse.name ?? null;
      selectedWarehouseTimezone.value = warehouse.timezone || null;
    } else {
      selectedWarehouseId.value = null;
      selectedWarehouseName.value = null;
      selectedWarehouseTimezone.value = null;
    }
  };

  const clearSelectedWarehouse = () => {
    selectedWarehouseId.value = null;
    selectedWarehouseName.value = null;
    selectedWarehouseTimezone.value = null;
  };

  const cleanup = () => {
    disposeId();
    disposeName();
    disposeTimezone();
  };

  return {
    selectedWarehouseId,
    selectedWarehouseName,
    selectedWarehouseTimezone,
    setSelectedWarehouse,
    clearSelectedWarehouse,
    cleanup
  };
});

export type IWarehouseStore = {
  selectedWarehouseId: Ref<string | null>;
  selectedWarehouseName: Ref<string | null>;
  selectedWarehouseTimezone: Ref<string | null>;
  setSelectedWarehouse: (warehouse: Partial<IWarehouse> | null) => void;
  clearSelectedWarehouse: () => void;
  cleanup: () => void;
};
